/**
 * Per-endpoint identity policy: the registry a feature declares against, and the two standing listeners that
 * reconcile RTKQ caches when the app commits an identity change.
 *
 * The folder: `./contract` (the declaration types a feature is typed against), `./rtk.access` (the single RTK
 * cast boundary), `./registry` (registry state, declaration validation, coverage reporting),
 * `./commit.reconcile` (the commit-time reconcile engine) and `./rejection.repair` (the fence-rejection repair
 * backstop). Dependencies point one way: contract <- rtk.access <- registry <- {commit.reconcile,
 * rejection.repair} <- here. `declareIdentityPolicy` — the one entry point a feature calls, and the arming
 * point for both listeners — lives here, next to the export surface it fronts.
 */

import { armIdentityCommitReconcile } from './commit.reconcile';
import type { IdentityPolicy, IdentityPolicyApi } from './contract';
import { getOrCreateApiEntry, readEndpointDefinitions, reportProblems, reportUndeclaredQueryEndpoints, validate } from './registry';
import { registerIdentityRejectionRepair } from './rejection.repair';

/**
 * Declare how each of a feature's endpoints reacts to an identity change, and validate the declaration.
 *
 * Call it once from the feature's data layer, right where the endpoints are injected. Validation runs at
 * DECLARATION time (so it fails in CI, not in production): a non-invariant endpoint must have its queryFn
 * wrapped in `identityScoped`, because an unfenced endpoint can write the previous identity's response into the
 * new identity's cache and no amount of reconciling after the fact repairs that. Nothing is required of
 * `providesTags`: the reconcile refetches each cached entry directly, by `initiate`, and never invalidates a tag
 * — tags stay entirely the feature's own business (its mutations' `invalidatesTags`). In dev and test builds a
 * violation THROWS; in production it is logged and the declaration still registers.
 *
 * COVERAGE is checked here too, and it is what makes a forgotten endpoint unshippable: after registering, every
 * query endpoint on the api with no policy — from this call or any earlier one — is reported. Because a feature
 * injects and declares back to back in one module body, at any declaration time every already-loaded feature has
 * finished its own declaration, so each feature is only ever accountable for what it injected. This runs at
 * IMPORT, so CI fails on the feature's own spec run; the commit-time sweep is only the runtime backstop.
 *
 * Declaring also ARMS both standing listeners (once each, app-wide): `registerIdentityRejectionRepair`, which
 * keeps a fenced rejection from coming to rest under a current generation, and `armIdentityCommitReconcile`, the
 * commit-time reconcile itself. Neither needs anything from the feature — the shared listener middleware is
 * installed by `StoreManager` and the commit predicate is injected at auth bootstrap
 * (`setIdentityCommitPredicate`) — so a declaration is the whole subscription, and no feature has to call
 * `registerAuthReconcile` merely to get its caches reconciled.
 *
 * @example
 * declareIdentityPolicy(featureApi, {
 *     getItems: { kind: 'purge', empty: () => [] },
 *     getItemCounters: { kind: 'purge', empty: () => ({}) },
 * });
 */
export function declareIdentityPolicy<Api extends IdentityPolicyApi>(
    api: Api,
    policies: { [Endpoint in keyof Api['endpoints']]?: IdentityPolicy },
): void {
    // Both armed HERE, from the first declaration, rather than from auth-core's wiring: a listener is only ever
    // useful once something is in the registry, so arming them off the registry's own entry point keeps them
    // exactly as lazy as the registry itself, and leaves auth-core with nothing to do but inject the seams.
    registerIdentityRejectionRepair();
    armIdentityCommitReconcile();
    const declared = Object.entries(policies as Record<string, IdentityPolicy | undefined>).filter(
        (entry): entry is [string, IdentityPolicy] => entry[1] !== undefined,
    );
    const definitions = readEndpointDefinitions(
        api,
        declared.map(([endpointName]) => endpointName),
    );
    const registered = getOrCreateApiEntry(api);
    const problems: string[] = [];

    for (const [endpointName, policy] of declared) {
        const definition = definitions[endpointName];
        registered.set(endpointName, policy);
        problems.push(...validate(endpointName, policy, definition));
    }

    reportProblems('Invalid identity policy declaration', problems);
    // The same single reporter the commit-time sweep uses, so both gates name the gap identically; the gap it
    // returns is only of interest to that sweep (which repairs it best-effort in production).
    reportUndeclaredQueryEndpoints(api, registered);
}

export {
    __resetIdentityCommitReconcileForTests,
    __resetIdentityReconcileDedupeForTests,
    armIdentityCommitReconcile,
    runIdentityCommitReconcile,
} from './commit.reconcile';
export type { IdentityEndpointDefinition, IdentityPolicy, IdentityPolicyApi, IdentityPurgeEmpty } from './contract';
export { __resetIdentityRejectionRepairForTests, registerIdentityRejectionRepair } from './rejection.repair';
